/**
 * 阿里云 Captcha 2.0 验证码 Composable
 * 
 * 使用方式：
 *   const { requestCaptchaVerify, destroyCaptcha } = useCaptcha()
 *   const captchaMessage = await requestCaptchaVerify(captchaKey)
 */
import { ref, onUnmounted } from 'vue'

export function useCaptcha() {
    // 验证码实例引用
    const captchaInstance = ref(null)
    // 当前是否正在等待验证
    const isCaptchaLoading = ref(false)

    /**
     * 初始化并弹出验证码，返回 Promise
     * @param {string} captchaKey - 后端返回的 captchaKey，作为 UserCertifyId 传给阿里云
     * @returns {Promise<string>} captchaVerifyParam 验签参数
     */
    function requestCaptchaVerify(captchaKey) {
        return new Promise((resolve, reject) => {
            // 先销毁之前的实例
            destroyCaptcha()
            isCaptchaLoading.value = true

            if (typeof window.initAliyunCaptcha !== 'function') {
                isCaptchaLoading.value = false
                reject(new Error('阿里云验证码SDK未加载，请刷新页面重试'))
                return
            }

            try {
                window.AliyunCaptchaConfig = {
                    region: "cn",
                    prefix: "bijl1j",
                    ...(window.AliyunCaptchaConfig || {}) // 合并全局可能已有的配置
                };

                window.initAliyunCaptcha({
                    SceneId: 'skgwj632',
                    mode: 'popup',
                    element: '#captcha-element',
                    button: '#captcha-button',
                    // 根据 V3 文档，UserCertifyId 必须严格按照 "{prefix}_{自定义ID}" 格式填报
                    UserCertifyId: `${window.AliyunCaptchaConfig.prefix}_${captchaKey}`,
                    success: (captchaVerifyParam) => {
                        console.log('captcha success:', captchaVerifyParam)
                        isCaptchaLoading.value = false
                        resolve(captchaVerifyParam)
                    },
                    fail: (err) => {
                        console.error('captcha fail:', err)
                    },
                    onError: (err) => {
                        console.error('captcha init error:', err)
                        isCaptchaLoading.value = false
                        reject(new Error('验证码资源加载失败: ' + (err.msg || err.code || '')))
                    },
                    getInstance: (instance) => {
                        captchaInstance.value = instance
                        setTimeout(() => {
                            const btn = document.getElementById('captcha-button')
                            if (btn) btn.click()
                        }, 100)
                    },
                    slideStyle: {
                        width: 360,
                        height: 40,
                    },
                    language: 'cn',
                })
            } catch (err) {
                isCaptchaLoading.value = false
                reject(err)
            }
        })
    }

    /**
     * 销毁验证码实例，清理 DOM
     */
    function destroyCaptcha() {
        if (captchaInstance.value) {
            try {
                // 阿里云 SDK 提供的销毁方法
                if (typeof captchaInstance.value.destroy === 'function') {
                    captchaInstance.value.destroy()
                }
            } catch (e) {
                console.warn('销毁验证码实例失败:', e)
            }
            captchaInstance.value = null
        }
        // 清理 DOM 残留
        const el = document.getElementById('captcha-element')
        if (el) {
            el.innerHTML = ''
        }
        isCaptchaLoading.value = false
    }

    // 组件卸载时自动清理
    onUnmounted(() => {
        destroyCaptcha()
    })

    return {
        requestCaptchaVerify,
        destroyCaptcha,
        isCaptchaLoading,
    }
}
